import React, { useState, useEffect } from 'react';
import { Coupon } from './types/Coupon';
import { Dashboard } from './components/Dashboard';
import { CouponInput } from './components/CouponInput';
import { CouponList } from './components/CouponList';
import Menu from './components/Menu';
import { loadTemplateData } from './data/templateData';
import './App.css';

function App() {
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [editingCoupon, setEditingCoupon] = useState<Coupon | undefined>();
  type ViewType = 'dashboard' | 'coupon-input' | 'coupon-list';
  const [currentView, setCurrentView] = useState<ViewType>('dashboard');
  const [isMenuOpen, setIsMenuOpen] = useState(true);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);

  // ローカルストレージからデータを読み込み、なければテンプレートデータを使用
  useEffect(() => {
    const savedCoupons = localStorage.getItem('coupon-management-coupons');
    
    if (savedCoupons) {
      // 保存されたデータがある場合は復元
      const parsedCoupons = JSON.parse(savedCoupons).map((c: any) => ({
        ...c,
        startDate: new Date(c.startDate),
        endDate: new Date(c.endDate),
        createdAt: new Date(c.createdAt),
        updatedAt: new Date(c.updatedAt)
      }));
      setCoupons(parsedCoupons);
    } else {
      // 初回起動時はテンプレートデータを使用
      const templateData = loadTemplateData();
      setCoupons(templateData);
      
      // テンプレートデータをローカルストレージに保存
      localStorage.setItem('coupon-management-coupons', JSON.stringify(templateData));
    }
  }, []);

  // データをローカルストレージに保存
  useEffect(() => {
    localStorage.setItem('coupon-management-coupons', JSON.stringify(coupons));
  }, [coupons]);

  // ユーザーメニューの外側をクリックしたときに閉じる
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (isUserModalOpen && !target.closest('.user-menu-container')) {
        setIsUserModalOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserModalOpen]);

  const handleSaveCoupon = (couponData: Omit<Coupon, 'id' | 'createdAt' | 'updatedAt' | 'usedCount'>) => {
    if (editingCoupon) {
      // 編集モード: 既存のクーポンを更新
      const updatedCoupon: Coupon = {
        ...couponData,
        id: editingCoupon.id,
        usedCount: editingCoupon.usedCount,
        createdAt: editingCoupon.createdAt,
        updatedAt: new Date()
      };
      
      setCoupons(prev => prev.map(c => c.id === editingCoupon.id ? updatedCoupon : c));
      setEditingCoupon(undefined);
    } else {
      // 新規作成モード
      const newCoupon: Coupon = {
        ...couponData,
        id: Date.now().toString(),
        usedCount: 0,
        createdAt: new Date(),
        updatedAt: new Date()
      };
      
      setCoupons(prev => [...prev, newCoupon]);
    }
    
    setCurrentView('coupon-list' as ViewType);
  };

  const handleDeleteCoupon = (id: string) => {
    if (window.confirm('このクーポンを削除しますか？')) {
      setCoupons(prev => prev.filter(c => c.id !== id));
      if (editingCoupon?.id === id) {
        setEditingCoupon(undefined);
      }
    }
  };

  const handleSelectCoupon = (coupon: Coupon) => {
    setEditingCoupon(coupon);
    setCurrentView('coupon-input' as ViewType);
  };

  const handleResetToTemplate = () => {
    if (window.confirm('すべてのデータを削除してテンプレートデータに戻しますか？この操作は取り消せません。')) {
      const templateData = loadTemplateData();
      setCoupons(templateData);
      setEditingCoupon(undefined);
      setCurrentView('dashboard' as ViewType);
      
      // ローカルストレージもリセット
      localStorage.setItem('coupon-management-coupons', JSON.stringify(templateData));
    }
  };

  const handleNewCoupon = () => {
    setEditingCoupon(undefined);
    setCurrentView('coupon-input' as ViewType);
  };

  return (
    <div className="App">
      <Menu
        activeTab={currentView as 'dashboard' | 'coupon-input' | 'coupon-list'}
        setActiveTab={(view) => setCurrentView(view as ViewType)}
        isOpen={isMenuOpen}
        onToggle={() => setIsMenuOpen(!isMenuOpen)}
        onNewProject={handleNewCoupon}
        onResetToTemplate={handleResetToTemplate}
      />

      <div className={`app-content ${isMenuOpen ? 'menu-open' : 'menu-closed'}`}>
        <header className="app-header">
          <h1>クーポン管理システム</h1>
          <div className="header-stats">
            <div className="header-user">
              <div className="user-menu-container">
                <div className="user-info" onClick={() => setIsUserModalOpen((v) => !v)} aria-haspopup="menu" aria-expanded={isUserModalOpen}>
                  <div className="user-details">
                    <span className="user-role">システム管理者</span>
                    <span className="user-caret">{isUserModalOpen ? '▲' : '▼'}</span>
                  </div>
                </div>
                {isUserModalOpen && (
                  <div className="user-menu" role="menu">
                    <div className="user-menu-header">システム太郎</div>
                    <button className="user-menu-item" role="menuitem">マイページ</button>
                    <div className="user-menu-sep" />
                    <button className="user-menu-item" role="menuitem">個人設定</button>
                    <button className="user-menu-item" role="menuitem">パスワード変更</button>
                    <button className="user-menu-item" role="menuitem">メールアドレス変更</button>
                    <button className="user-menu-item" role="menuitem">ソーシャルログイン設定</button>
                    <div className="user-menu-sep" />
                    <button className="user-menu-item danger" role="menuitem" onClick={() => setIsUserModalOpen(false)}>ログアウト</button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        <main className="app-main">
          {currentView === ('dashboard' as ViewType) ? (
            <Dashboard coupons={coupons} />
          ) : currentView === ('coupon-input' as ViewType) ? (
            <CouponInput
              onSubmit={handleSaveCoupon}
              onCancel={() => {
                setEditingCoupon(undefined);
                setCurrentView('coupon-list' as ViewType);
              }}
              editingCoupon={editingCoupon}
            />
          ) : currentView === ('coupon-list' as ViewType) ? (
            <CouponList
              coupons={coupons}
              onSelectCoupon={handleSelectCoupon}
              onDeleteCoupon={handleDeleteCoupon}
              onAddCoupon={handleNewCoupon}
            />
          ) : null}
        </main>
      </div>
    </div>
  );
}

export default App;